import { ColorSchemeContext } from '@common/core/color-scheme-provider';
import { LocaleSwitcher } from '@common/locale-switcher/locale-switcher';
import { Button } from '@ui/buttons/button';
import { Trans } from '@ui/i18n/trans';
import { LucideIcon } from '@ui/icons/lucide/lucide-icon-wrapper';
import { FacebookIcon } from '@ui/icons/social/facebook';
import { InstagramIcon } from '@ui/icons/social/instagram';
import { TwitterIcon } from '@ui/icons/social/twitter';
import { YoutubeIcon } from '@ui/icons/social/youtube';
import { useSettings } from '@ui/settings/use-settings';
import clsx from 'clsx';
import { MoonIcon, SunIcon } from 'lucide-react';
import { use } from 'react';
import { CustomMenu, CustomMenuItem } from '../../menus/custom-menu';

interface Props {
  className?: string;
  padding?: string;
}

export function Footer({className, padding}: Props) {
  const year = new Date().getFullYear();
  const {branding} = useSettings();
  return (
    <footer
      className={clsx(
        'text-sm',
        padding ? padding : 'pt-13.5 pb-7 md:pb-13.5',
        className,
      )}
    >
      <Menus />
      <div className="text-muted-foreground items-center justify-between gap-7.5 text-center md:flex md:text-left">
        <Trans
          message="Copyright © :year :name, All Rights Reserved"
          values={{year, name: branding.site_name}}
        />
        <div>
          <ThemeSwitcher />
          <LocaleSwitcher />
        </div>
      </div>
    </footer>
  );
}

function Menus() {
  const settings = useSettings();
  const primaryMenu = settings.menus.find(m => m.positions?.includes('footer'));
  const secondaryMenu = settings.menus.find(m =>
    m.positions?.includes('footer-secondary'),
  );

  if (!primaryMenu && !secondaryMenu) return null;

  return (
    <div className="mb-3.5 items-center justify-between gap-7.5 overflow-x-auto border-b pb-3.5 md:flex">
      {primaryMenu && (
        <CustomMenu menu={primaryMenu} className="text-primary" />
      )}
      {secondaryMenu && (
        <CustomMenu menu={secondaryMenu} className="mb:mt-0 text-muted-foreground mt-3.5">
          {(item, props) => {
            const icon =
              typeof item.icon === 'string' ? (
                <SocialIcon icon={item.icon} />
              ) : null;
            return (
              <CustomMenuItem
                key={item.id}
                {...props}
                item={item}
                icon={icon}
              />
            );
          }}
        </CustomMenu>
      )}
    </div>
  );
}

interface SocialIconProps {
  icon: string;
}
function SocialIcon({icon}: SocialIconProps) {
  switch (icon) {
    case 'facebook':
      return <FacebookIcon size="sm" />;
    case 'twitter':
      return <TwitterIcon size="sm" />;
    case 'instagram':
      return <InstagramIcon size="sm" />;
    case 'youtube':
      return <YoutubeIcon />;
    default:
      return null;
  }
}

function ThemeSwitcher() {
  const {themes} = useSettings();
  const {colorScheme, setColorScheme} = use(ColorSchemeContext);
  if (!themes?.user_change) return null;

  return (
    <Button
      variant="text"
      startIcon={
        colorScheme === 'dark' ? (
          <LucideIcon icon={MoonIcon} size="xs" />
        ) : (
          <LucideIcon icon={SunIcon} size="xs" />
        )
      }
      onClick={() => {
        setColorScheme(colorScheme === 'light' ? 'dark' : 'light');
      }}
    >
      {colorScheme === 'dark' ? (
        <Trans message="Light mode" />
      ) : (
        <Trans message="Dark mode" />
      )}
    </Button>
  );
}
